import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TaskBodyProps } from './index';
import type { OutgoingAction } from '../../../services/workQueue';
import { useToast } from '../../../providers/ToastProvider';
import { reportError } from '../../../services/errorReporting';
import { invalidateCaseDetail } from '../util';
import { apiErrorMessage } from '../../../utils/apiError';

// Action ids in flight / completed, so several action buttons can coexist.
export interface ActionProgress {
    pending: string | null;
    succeeded: string | null;
}

// Runs a body-owned workflow action and then completes the task the way the surface footer does.
export function useReviewActionRunner(
    caseId: string,
    onTaskComplete: TaskBodyProps['onTaskComplete'],
    beforeAction: (() => Promise<void>) | undefined
) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const [progress, setProgress] = useState<ActionProgress>({ pending: null, succeeded: null });

    const runAction = async (
        action: OutgoingAction,
        perform: () => Promise<unknown>,
        source: string,
        fallbackMessage: string
    ): Promise<void> => {
        setProgress({ pending: action.action, succeeded: null });
        try {
            await beforeAction?.();
            await perform();
            await invalidateCaseDetail(queryClient, caseId);
            setProgress({ pending: action.action, succeeded: action.action });
            onTaskComplete?.(action);
        } catch (err) {
            const message = apiErrorMessage(err, fallbackMessage);
            showToast(message, 'error');
            reportError({
                source,
                message,
                stack: err instanceof Error ? err.stack : undefined,
                url: globalThis.location?.href ?? '',
                userAgent: navigator.userAgent,
                errorType: 'workflow_action_failure',
                metadata: { caseId, action: action.action }
            });
            setProgress({ pending: null, succeeded: null });
        }
    };

    return { progress, runAction };
}
